import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { localStorageManager } from '../../services/LocalStorageManager';

interface DisclaimerDialogProps {
  open: boolean;
  onClose: () => void;
  requireAcknowledgment?: boolean;
}

/**
 * Dialog for displaying the prediction limitations disclaimer
 */
const DisclaimerDialog: React.FC<DisclaimerDialogProps> = ({
  open,
  onClose,
  requireAcknowledgment = false
}) => {
  const [acknowledged, setAcknowledged] = useState(!requireAcknowledgment);

  const handleAcknowledge = () => {
    if (requireAcknowledgment) {
      try {
        localStorage.setItem('tideAnalyzer.disclaimerAcknowledged', 'true');
      } catch (error) {
        console.error('Error setting disclaimer acknowledgment:', error);
      }
    }
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={requireAcknowledgment ? undefined : onClose} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        Important Disclaimer: Tide Prediction Limitations
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          The tide predictions provided by this application are based on mathematical modeling 
          and historical data. While we strive for accuracy, predictions are subject to the 
          following limitations:
        </Typography>
        
        <Box sx={{ my: 2 }}>
          <Typography variant="body1" component="div">
            <ul>
              <li>
                <strong>Prediction Accuracy:</strong> Tide predictions use cosine interpolation based on 
                data from WillyWeather API. This method assumes regular tidal patterns and may be less 
                accurate during irregular conditions.
              </li>
              <li>
                <strong>Weather Effects:</strong> Predictions do not account for abnormal weather conditions 
                such as storms, atmospheric pressure changes, or strong winds, which can significantly alter 
                actual tide heights and timing.
              </li>
              <li>
                <strong>Geographic Variations:</strong> The accuracy of predictions may vary by location. 
                Areas with complex coastal geography, river inflows, or unusual tidal patterns may experience 
                greater variance between predicted and actual tide conditions.
              </li>
              <li>
                <strong>Time Limitations:</strong> Predictions become less reliable the further they extend 
                from the current date due to cumulative effects of environmental variables.
              </li>
              <li>
                <strong>External Factors:</strong> Local conditions including underwater topography, harbor 
                resonance effects, and seasonal variations are not fully accounted for in the prediction model.
              </li>
              <li>
                <strong>Service Scheduling:</strong> While the application identifies potential service windows 
                based on tide conditions, users should verify conditions independently before scheduling critical 
                maritime activities.
              </li>
            </ul>
          </Typography>
        </Box>
        
        <Typography variant="body1" color="error" paragraph>
          This application is provided for informational and planning purposes only. All maritime activities 
          should be conducted with appropriate caution and in consideration of current observed conditions. 
          Users should consult official navigation notices, weather forecasts, and local maritime authorities 
          before undertaking any water-based activities.
        </Typography>
        
        <Typography variant="body2" color="textSecondary">
          The developers of Tide Analyzer accept no liability for decisions made or actions taken based on 
          the information provided by this application.
        </Typography>
        
        {requireAcknowledgment && (
          <FormControlLabel
            control={
              <Checkbox 
                checked={acknowledged} 
                onChange={(e) => setAcknowledged(e.target.checked)} 
              />
            }
            label="I have read and understand these limitations"
            sx={{ mt: 2 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        {requireAcknowledgment ? (
          <Button 
            onClick={handleAcknowledge} 
            color="primary" 
            disabled={!acknowledged}
          >
            I Acknowledge
          </Button>
        ) : (
          <Button onClick={onClose} color="primary">Close</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DisclaimerDialog;
